import React from 'react';
import clsx from 'clsx';

// Shared chrome for every page under Settings. Before this, each page drew its own header —
// some with an icon tile, some with a gradient, some centred at 4xl, some full-bleed — so moving
// between Branding, Features and Pay Groups felt like moving between products. One container
// width, one header, one switch.

// Page container: same left edge and width on every settings page. `.settings-root` themes the
// browser-drawn parts of forms (select chevron, checkbox, focus ring) — see index.css.
// <main> already pads the page, so no page adds its own outer padding.
export const PAGE = 'settings-root mx-auto w-full max-w-6xl space-y-6';
// Data screens (registers, boards, reports) use the full width; forms and settings stay at PAGE.
export const PAGE_WIDE = 'settings-root w-full space-y-6';

export function SettingsPage({ children, className, wide = false }) {
  return <div className={clsx(wide ? PAGE_WIDE : PAGE, className)}>{children}</div>;
}

// The page title block. The app header above already says "Settings", so this names the page
// itself: title, one line of what it controls, and the page's actions on the right.
export function SettingsHeader({ title, description, actions, before, after }) {
  return (
    <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {before}
        <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em] text-slate-900 [text-wrap:balance]">
          {title}
        </h1>
        {description && (
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-500 [text-wrap:pretty]">{description}</p>
        )}
        {after}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

// On/off switch. `decorative` renders a non-interactive copy for use inside a larger control
// that already owns the click (a whole card acting as the switch).
export function Switch({ checked, onChange, disabled, label, decorative = false, className }) {
  const track = clsx(
    'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-150 ease-out',
    checked ? 'bg-primary-600' : 'bg-slate-300',
    disabled && 'opacity-50',
    className,
  );
  const knob = (
    <span
      aria-hidden="true"
      className={clsx(
        'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-[0_1px_2px_rgba(16,24,40,.2)] transition-transform duration-150 ease-out',
        checked ? 'translate-x-[18px]' : 'translate-x-0.5',
      )}
    />
  );
  if (decorative) return <span aria-hidden="true" className={track}>{knob}</span>;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={clsx(
        track,
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      )}
    >
      {knob}
    </button>
  );
}
